import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { auth, db } from "../../firebaseConfig";
import {
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, collection } from "firebase/firestore";
import { SnackbarContext } from "../../SnackbarContext";
import ConfirmationDialog from "../ConfirmationDialog";
import { useAuth } from "../../auth/AuthProvider";
import { generateInviteCodeNormalized } from "../../utils/inviteCode";

export default function SignIn({ switchToSignUp }) {
  const [loading, setLoading] = useState(false);
  const { showSuccess, showError } = useContext(SnackbarContext);
  const { googleSignIn } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
const toggleShowPassword = () => setShowPassword(prev => !prev);

  // NEW: state for the “enter your email” dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetEmailInput, setResetEmailInput] = useState("");

  const createFarmDoc = async (displayName, userUid, photoURL = "", email = "") => {
    const farmDocRef = doc(collection(db, "farms"));
    const farmId = farmDocRef.id;

    await setDoc(farmDocRef, {
      farmName: displayName ? `${displayName}'s Farm` : null,
      createdBy: userUid,
      createdAt: serverTimestamp(),
      inviteCode: generateInviteCodeNormalized(),
      ownerIds: [userUid],
      adminIds: [],
      settings: {
        weightUnit: "g",
        measureUnit: "cm",
        dateFormat: "dd-MM-yyyy",
        calendarWeekStart: "monday",
        currency: "£",
      },
      address: null,
      logoFileName: null,
      logoUrl: null,
      phone: null,
      email: email || "",
    });

    await setDoc(doc(db, "farms", farmId, "members", userUid), {
      uid: userUid,
      displayName: displayName || "",
      photoURL: photoURL || "",
      role: "owner",
      status: "active",
      invitedBy: userUid,
      invitedAt: serverTimestamp(),
      joinedAt: serverTimestamp(),
    });

    return farmId;
  };

  const ensureGoogleUserDoc = async (user) => {
    const userDocRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userDocRef);
    if (!userSnap.exists()) {
      const farmId = await createFarmDoc(user.displayName || "", user.uid, user.photoURL || "", user.email || "");
      await setDoc(userDocRef, {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || "",
        createdAt: serverTimestamp(),
        approved: false,
        accessExpires: new Date(Date.now() - 1000),
        activeFarmId: farmId,
      });
      await setDoc(
        doc(db, "users", user.uid, "counters", "orders"),
        { orderCount: 0 },
        { merge: true }
      );
    } else if (!userSnap.data()?.activeFarmId) {
      const farmId = await createFarmDoc(user.displayName || "", user.uid, user.photoURL || "", user.email || "");
      await setDoc(
        userDocRef,
        { activeFarmId: farmId },
        { merge: true }
      );
    }
  };

  // ---------------------------------------
  // Helper: Basic client‐side validation
  // ---------------------------------------
  const validateInputs = () => {
    const email = document.getElementById("email").value.trim();
    const password = document.getElementById("password").value;

    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showError("Please enter a valid email address.");
      return false;
    }
    if (!password || password.length < 6) {
      showError("Password must be at least 6 characters long.");
      return false;
    }
    return true;
  };

  // ---------------------------------------
  // Email/Password Sign‐In
  // ---------------------------------------
  const handleEmailSignIn = async (event) => {
    event.preventDefault();
    if (!validateInputs()) return;

    const email = document.getElementById("email").value.trim();
    const password = document.getElementById("password").value;
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      showSuccess("Logged in successfully.");
      // …redirect or close modal, etc.
    } catch (error) {
      console.error("Error signing in:", error.code);
      if (error.code === "auth/user-not-found") {
        showError("No user found with that email.");
      } else if (error.code === "auth/wrong-password") {
        showError("Incorrect password.");
      } else {
        showError("Failed to sign in. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  // ---------------------------------------
  // Google Sign In
  // ---------------------------------------
  const handleGoogleSignIn = async (event) => {
    event.preventDefault();
    setLoading(true);

    try {
      const result = await googleSignIn();
      if (!result?.user) return;
      await ensureGoogleUserDoc(result.user);
      showSuccess("Signed in with Google successfully.");
    } catch (error) {
      console.error("Google sign-in failed:", error?.code || error, error);
      showError(`Failed to sign in with Google: ${error?.code || "unknown-error"}`);
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------
  // Open/close the “enter your email” dialog
  // ---------------------------------------
  const openResetDialog = () => {
    setResetEmailInput("");    // clear any previous value
    setResetDialogOpen(true);
  };
  const closeResetDialog = () => {
    setResetDialogOpen(false);
  };

  // ---------------------------------------
  // Called when the user clicks “Accept” in the dialog
  // resetEmailInput is the email they typed in.
  // ---------------------------------------
  const handleConfirmReset = async (inputValue) => {
    // inputValue is what they typed into the dialog’s TextField
    const email = inputValue.trim();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      showError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      await sendPasswordResetEmail(auth, email);
      showSuccess(`Reset link sent to ${email}. Check your inbox.`);
      closeResetDialog();
    } catch (err) {
      console.error("Error sending password reset email:", err.code);
      if (err.code === "auth/user-not-found") {
        showError("No account found with that email.");
      } else {
        showError("Failed to send reset email. Try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: 450, padding: 4 }}>
          <Typography component="h1" variant="h5" align="center">
            Login
          </Typography>

          {/* ---------------------------------------------
              Email/Password Form
          --------------------------------------------- */}
          <Box
            component="form"
            onSubmit={handleEmailSignIn}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              marginTop: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="email">Email</FormLabel>
              <TextField
                required
                fullWidth
                id="email"
                name="email"
                placeholder="your@email.com"
                autoComplete="email"
                variant="outlined"
                sx={{ backgroundColor: "transparent" }}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="password">Password</FormLabel>
              <TextField
                required
                fullWidth
                name="password"
type={showPassword ? "text" : "password"}
InputProps={{
  endAdornment: (
    <InputAdornment position="end">
      <IconButton onClick={toggleShowPassword} edge="end">
        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </IconButton>
    </InputAdornment>
  )
}}
                id="password"
                placeholder="••••••••••••"
                autoComplete="current-password"
                variant="outlined"
                sx={{ backgroundColor: "transparent" }}
              />
            </FormControl>

            <Button
              type="submit"
              fullWidth
              color="secondary"
              variant="contained"
              disabled={loading}
              sx={{ mt: 2 }}
            >
              Login
            </Button>
          </Box>

          {/* ---------------------------------------------
              OR Divider
          --------------------------------------------- */}
          <Typography align="center" sx={{ mt: 2 }}>
            OR
          </Typography>

          {/* ---------------------------------------------
              Google Sign‐In Button
          --------------------------------------------- */}
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleGoogleSignIn}
            disabled={loading}
            sx={{ mt: 2 }}
          >
            Sign in with Google
          </Button>

          {/* ---------------------------------------------
              “Sign Up” Link
          --------------------------------------------- */}
          <Typography sx={{ textAlign: "center", mt: 3 }}>
            Don’t have an account?{" "}
            <Button
              onClick={switchToSignUp}
              variant="text"
              sx={{ textTransform: "none", p: 0 }}
            >
              Sign Up
            </Button>
          </Typography>

          {/* ---------------------------------------------
              “Forgotten your password?” Link
              → Opens ConfirmationDialog instead of reading from the form
          --------------------------------------------- */}
          <Typography sx={{ textAlign: "center", mt: 1 }}>
            Forgotten your password?{" "}
            <Button
              onClick={openResetDialog}
              variant="text"
              sx={{ textTransform: "none", p: 0 }}
              disabled={loading}
            >
              Reset Password
            </Button>
          </Typography>
        </Box>
      </Box>

      {/* =============================================
          ConfirmationDialog for “type your email”
          ============================================= */}
      <ConfirmationDialog
        open={resetDialogOpen}
        title="Reset Password"
        message="Please enter your email address to receive a reset link."
        showInput={true}
        onClose={closeResetDialog}
        onConfirm={(inputValue) => handleConfirmReset(inputValue)}
      />
    </>
  );
}





